/** @jsxImportSource @emotion/react */
import { css, jsx } from '@emotion/react';
import React from 'react';

import { Colors } from 'src/common/ui/base/Colors';
import { Spacing } from 'src/common/ui/base/Spacing';
import { TypographyStyles } from 'src/common/ui/base/Typography';
import { BoulderIcon } from 'src/common/ui/components/BoulderIcon';

/**
 * Chip component for displaying labels, values, and an optional delete icon.
 *
 * @param component - The HTML element type for the chip (e.g., button, div, etc.).
 * @param children - Additional content to render inside the chip.
 * @param label - The label text to display in the chip.
 * @param onClick - The click event handler for the chip.
 * @param onDeleteClick - The click event handler for the delete icon.
 * @param value - The value text to display in the chip.
 */
export const Chip = ({
  children,
  component = 'button',
  label,
  onClick,
  onDeleteClick,
  value,
}: React.PropsWithChildren<{
  component?: 'button' | 'a' | 'div' | 'span';
  children?: React.ReactNode;
  label?: string;
  onClick?: React.MouseEventHandler<
    HTMLElement | HTMLButtonElement | HTMLAnchorElement | HTMLDivElement | HTMLSpanElement
  >;
  onDeleteClick?: React.MouseEventHandler<HTMLButtonElement>;
  value?: string;
}>) => {
  if (onClick && onDeleteClick) {
    console.warn(
      "Both onClick and onDeleteClick handlers were provided to the Chip component, which isn't supported.",
    );
    return null;
  }

  return jsx(
    component,
    {
      css: chipStyles,
      'data-button': onClick ? true : undefined,
      'data-testid': 'nds-component-chip',
      disabled: component === 'button' ? !onClick : undefined,
      onClick,
      tabIndex: onClick ? undefined : -1,
      type: component === 'button' ? 'button' : undefined,
    },
    <>
      {label && <h6 css={chipLabelStyles}>{label}</h6>}
      {value && <span css={chipValueStyles}>{value}</span>}
      {children}
      {onDeleteClick && (
        <button css={chipIconButtonStyles} type="button" onClick={onDeleteClick}>
          <BoulderIcon icon="delete" size={18} css={chipIconStyles} />
        </button>
      )}
    </>,
  );
};

const chipStyles = css`
  ${TypographyStyles.body1};

  background: ${Colors.CoolGray2};
  border-radius: ${Spacing.Unit3}px;
  border: none;
  margin-left: ${Spacing.Unit2}px;
  padding: ${Spacing.Unit1}px ${Spacing.Unit2 + 2}px;
  transition: background 0.1s ease-in-out;

  &:first-child {
    margin-left: 0;
  }

  &[data-button] {
    cursor: pointer;

    &:hover {
      background: ${Colors.CoolGray3};
    }

    &:active {
      background: ${Colors.CoolGray4};
    }

    &:focus {
      outline: 2px solid ${Colors.Blue5};
    }
  }
`;

const chipLabelStyles = css`
  ${TypographyStyles.subtitle2};

  display: inline;
  margin: 0;
  padding: 0;
`;

const chipValueStyles = css`
  ${TypographyStyles.body1};

  margin-left: ${Spacing.Unit1}px;

  &:first-child {
    margin-left: 0;
  }
`;

const chipIconButtonStyles = css`
  background: transparent;
  border: none;
  cursor: pointer;
  margin: 0;
  margin-left: ${Spacing.Unit2}px;
  padding: 0;
  position: relative;
  top: -1px;
  width: 18px;
  height: 18px;
  display: inline-block;
  vertical-align: middle;

  &:first-child {
    margin-left: 0;
  }

  &:last-child {
    margin-right: -2px;
  }

  &:hover {
    svg path {
      fill: ${Colors.CoolGray8};
    }
  }

  &:active {
    svg path {
      fill: ${Colors.CoolGray9};
    }
  }

  &:focus {
    outline: 2px solid ${Colors.Blue5};
  }
`;

const chipIconStyles = css`
  display: block;

  path {
    fill: ${Colors.CoolGray5};
    transition: fill 0.1s ease-in-out;
  }
`;
